import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Divider,
  InputAdornment,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { getOptimalParametersForTaskCount } from '../../utils/parameterUtils';
import { FitnessMode, EnergyAwareWeights } from '../../types';

interface ParameterPanelProps {
  initialValues?: {
    taskCount?: number;
    qosProperties?: number;
    iterations?: number;
    initialPopulation?: number;
    selectionRate?: number;
    crossoverRate?: number;
    mutationRate?: number;
    memeplexCount?: number;
    numberOfRuns?: number;
    fitnessMode?: FitnessMode;
    energyAwareWeights?: EnergyAwareWeights;
  };
  onParametersChange?: (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
    fitnessMode?: FitnessMode;
    energyAwareWeights?: EnergyAwareWeights;
  }) => void;
}

const ParameterPanel: React.FC<ParameterPanelProps> = ({ initialValues, onParametersChange }) => {
  // Initialize with optimal parameters based on taskCount if no initialValues provided
  const initialTaskCount = initialValues?.taskCount ?? 10;
  const optimalParams = getOptimalParametersForTaskCount(initialTaskCount);

  const [parameters, setParameters] = useState({
    taskCount: initialTaskCount,
    qosProperties: initialValues?.qosProperties ?? 9,
    iterations: initialValues?.iterations ?? Math.floor(initialTaskCount / 10) + 40, // iterations = (workflow tasks / 10) + 40
    initialPopulation: initialValues?.initialPopulation ?? (initialTaskCount + 40), // initial population = workflow tasks + 40
    selectionRate: initialValues?.selectionRate ?? 60, // 60% for GA/Cultural (SFLAGA uses tournament directly)
    crossoverRate: initialValues?.crossoverRate ?? optimalParams.crossoverRate,
    mutationRate: Math.round(initialValues?.mutationRate ?? optimalParams.mutationRate), // Always rounded
    memeplexCount: initialValues?.memeplexCount ?? optimalParams.memeplexCount,
    numberOfRuns: initialValues?.numberOfRuns ?? 5, // number of runs = 5
    fitnessMode: initialValues?.fitnessMode ?? 'energy-aware',
    energyAwareWeights: initialValues?.energyAwareWeights ?? { fitness: 0.5, energy: 0.3, cost: 0.2 },
  });

  // Update parameters when initialValues change (for preset loading and complete run updates)
  React.useEffect(() => {
    if (initialValues) {
      // Use all provided values - the context already calculates optimal parameters
      // Only update if values actually changed to avoid unnecessary re-renders
      setParameters(prev => {
        // Check if taskCount changed - if so, use all new values (they include optimal params)
        if (initialValues.taskCount !== undefined && initialValues.taskCount !== prev.taskCount) {
          const optimal = getOptimalParametersForTaskCount(initialValues.taskCount);
          return {
            taskCount: initialValues.taskCount,
            qosProperties: initialValues.qosProperties ?? prev.qosProperties,
            iterations: Math.floor(initialValues.taskCount / 10) + 40, // iterations = (workflow tasks / 10) + 40
            initialPopulation: initialValues.initialPopulation ?? (initialValues.taskCount + 40), // initial population = workflow tasks + 40
            selectionRate: initialValues.selectionRate ?? prev.selectionRate,
            crossoverRate: initialValues.crossoverRate ?? optimal.crossoverRate,
            mutationRate: Math.round(initialValues.mutationRate ?? optimal.mutationRate), // Always rounded
            memeplexCount: initialValues.memeplexCount ?? optimal.memeplexCount,
            numberOfRuns: 5, // number of runs = 5
            fitnessMode: initialValues.fitnessMode ?? prev.fitnessMode,
            energyAwareWeights: initialValues.energyAwareWeights ?? prev.energyAwareWeights,
          };
        }
        // Otherwise, merge with existing values
        return {
          ...prev,
          ...initialValues,
          // Ensure mutation rate is always rounded
          mutationRate: initialValues.mutationRate !== undefined 
            ? Math.round(initialValues.mutationRate) 
            : prev.mutationRate,
          // Preserve fitnessMode and energyAwareWeights if not provided
          fitnessMode: initialValues.fitnessMode ?? prev.fitnessMode,
          energyAwareWeights: initialValues.energyAwareWeights ?? prev.energyAwareWeights,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.taskCount, initialValues?.iterations, initialValues?.initialPopulation, 
      initialValues?.crossoverRate, initialValues?.mutationRate, initialValues?.memeplexCount]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: any = event.target.value;
    
    // Handle numeric fields
    if (['taskCount', 'qosProperties', 'iterations', 'initialPopulation', 'selectionRate', 
         'crossoverRate', 'mutationRate', 'memeplexCount', 'numberOfRuns'].includes(field)) {
      value = parseFloat(event.target.value) || 0;
      
      // Round mutation rate to nearest integer (no decimal values)
      if (field === 'mutationRate') {
        value = Math.round(value);
      }
    }
    
    // Handle fitness mode
    if (field === 'fitnessMode') {
      value = event.target.value as FitnessMode;
    }
    
    // Handle energy-aware weights
    if (field.startsWith('energyAwareWeights.')) {
      const weightField = field.split('.')[1] as keyof EnergyAwareWeights;
      const weightValue = parseFloat(event.target.value) || 0;
      const newWeights = { ...parameters.energyAwareWeights, [weightField]: weightValue };
      value = newWeights;
      field = 'energyAwareWeights';
    }
    
    let newParams = { ...parameters, [field]: value };
    
    // If taskCount changes, auto-adjust other parameters based on problem size
    if (field === 'taskCount') {
      const optimal = getOptimalParametersForTaskCount(value);
      newParams = {
        ...newParams,
        ...optimal,
        iterations: Math.floor(value / 10) + 40, // iterations = (workflow tasks / 10) + 40
        initialPopulation: value + 40, // initial population = workflow tasks + 40
        numberOfRuns: 10, // number of runs = 10
      };
    }
    
    setParameters(newParams);
    if (onParametersChange) {
      onParametersChange(newParams);
    }
  };

  // Initial notification
  React.useEffect(() => {
    if (onParametersChange) {
      onParametersChange(parameters);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
        <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Parameters
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Common Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Common Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Workflow Tasks"
            type="number"
            value={parameters.taskCount}
            onChange={handleChange('taskCount')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 1000 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="QoS Properties"
            type="number"
            value={parameters.qosProperties}
            onChange={handleChange('qosProperties')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 20 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Iterations"
            type="number"
            value={parameters.iterations}
            onChange={handleChange('iterations')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 10000 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Initial Population"
            type="number"
            value={parameters.initialPopulation}
            onChange={handleChange('initialPopulation')}
            size="small"
            fullWidth
            inputProps={{ min: 10, max: 10000 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* GA Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Genetic Algorithm (GA)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Selection Rate"
            type="number"
            value={parameters.selectionRate}
            onChange={handleChange('selectionRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Crossover Rate"
            type="number"
            value={parameters.crossoverRate}
            onChange={handleChange('crossoverRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Mutation Rate"
            type="number"
            value={Math.round(parameters.mutationRate)}
            onChange={handleChange('mutationRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* SFLA Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        SFLA Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Memeplex Count"
            type="number"
            value={parameters.memeplexCount}
            onChange={handleChange('memeplexCount')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Energy-Aware Optimization */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Energy-Aware Optimization
      </Typography>
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend">Fitness Calculation Mode</FormLabel>
        <RadioGroup
          row
          value={parameters.fitnessMode}
          onChange={(e) => {
            const newParams = { ...parameters, fitnessMode: e.target.value as FitnessMode };
            setParameters(newParams);
            if (onParametersChange) {
              onParametersChange(newParams);
            }
          }}
        >
          <FormControlLabel value="qos-only" control={<Radio size="small" />} label="QoS Only" />
          <FormControlLabel value="energy-aware" control={<Radio size="small" />} label="Energy-Aware" />
        </RadioGroup>
      </FormControl>

      {parameters.fitnessMode === 'energy-aware' && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Energy-aware mode optimizes for composite fitness: w_fitness × QoS + w_energy × Energy + w_cost × Cost
          </Alert>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="QoS Weight"
                type="number"
                value={parameters.energyAwareWeights.fitness}
                onChange={handleChange('energyAwareWeights.fitness')}
                size="small"
                fullWidth
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">w₁</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Energy Weight"
                type="number"
                value={parameters.energyAwareWeights.energy}
                onChange={handleChange('energyAwareWeights.energy')}
                size="small"
                fullWidth
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">w₂</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Cost Weight"
                type="number"
                value={parameters.energyAwareWeights.cost}
                onChange={handleChange('energyAwareWeights.cost')}
                size="small"
                fullWidth
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">w₃</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Weight Sum: {(parameters.energyAwareWeights.fitness + parameters.energyAwareWeights.energy + parameters.energyAwareWeights.cost).toFixed(2)}
              {Math.abs((parameters.energyAwareWeights.fitness + parameters.energyAwareWeights.energy + parameters.energyAwareWeights.cost) - 1.0) > 0.01 && (
                <span style={{ color: 'orange' }}> (will be normalized to 1.0)</span>
              )}
            </Typography>
          </Box>
        </>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Statistical Analysis Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Statistical Analysis
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Number of Runs"
            type="number"
            value={parameters.numberOfRuns}
            onChange={handleChange('numberOfRuns')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
            helperText="Multiple runs for statistical analysis"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParameterPanel;

